import { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface CookieConsentState {
  essential: boolean;
  analytics: boolean;
  consentGiven: boolean;
}

interface CookieConsentContextType {
  state: CookieConsentState;
  acceptAll: () => void;
  acceptEssential: () => void;
  toggleAnalytics: (value: boolean) => void;
  openSettings: () => void;
  closeSettings: () => void;
  settingsOpen: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | null>(null);

const STORAGE_KEY = 'ssg-cookie-consent';

function getInitialState(): CookieConsentState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as CookieConsentState;
      return parsed;
    }
  } catch {
    // ignore parse errors
  }
  return { essential: true, analytics: false, consentGiven: false };
}

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<CookieConsentState>(getInitialState);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore storage errors
    }
  }, [state]);

  const acceptAll = useCallback(() => {
    setState({ essential: true, analytics: true, consentGiven: true });
    setSettingsOpen(false);
  }, []);

  const acceptEssential = useCallback(() => {
    setState({ essential: true, analytics: false, consentGiven: true });
    setSettingsOpen(false);
  }, []);

  const toggleAnalytics = useCallback((value: boolean) => {
    setState(prev => ({ ...prev, analytics: value }));
  }, []);

  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  return (
    <CookieConsentContext.Provider
      value={{
        state,
        acceptAll,
        acceptEssential,
        toggleAnalytics,
        openSettings,
        closeSettings,
        settingsOpen,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextType {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return ctx;
}